import { useState } from "react";
import type { AnfrageRohdaten } from "@bidi-handel/core/schemas";
import { api } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";
import { PdfKlassifikationBox } from "./PdfKlassifikationBox";
import { PdfPreviewModal } from "./PdfPreviewModal";
import { LvTreeModal } from "./LvTreeModal";

/**
 * Uebersicht-Tab im Redesign-Look:
 *  1. PdfKlassifikationBox (oben, wenn unklassifizierte PDFs vorhanden)
 *  2. 4 Metric-Cards (Kundenartikel / Konditionen / Marge / Positionen)
 *  3. Source-Block (hochgeladene Dateien)
 *  4. Pipeline-Phasen-Liste
 *  5. Notice-Block (Konditionen-Check) + Ready-Block (Angebot-Download) nebeneinander
 */

const PHASES: Array<{ id: number; label: string; code: string; model: string }> = [
  { id: 0, label: "Anfrage-Rohdaten", code: "00", model: "—" },
  { id: 1, label: "Anfrage-Typ (Pfad A/B)", code: "01", model: "haiku" },
  { id: 2, label: "LV-Positionen", code: "02", model: "—" },
  { id: 3, label: "Konsolidierung", code: "03", model: "—" },
  { id: 4, label: "Kundenartikel-Match", code: "04", model: "sonnet" },
  { id: 5, label: "Bepreisung", code: "05", model: "—" },
  { id: 6, label: "Angebot · xlsx + pdf", code: "06", model: "haiku" },
];

export function UebersichtTab({
  tenantId,
  anfrageId,
  rohdaten,
  phasenVorhanden,
  onRunRequest,
  onRefetch,
}: {
  tenantId: string;
  anfrageId: string;
  rohdaten: AnfrageRohdaten;
  phasenVorhanden: number[];
  onRunRequest: () => void;
  onRefetch: () => void;
}) {
  const summary = useApi(
    () => api.getStammdatenSummary(tenantId),
    [tenantId]
  );
  const angebot = useApi(
    () => api.getAngebotManifest(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  const klaer = useApi(
    () => api.getKlaerpunkte(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  // Match + Preise fuer den QS-Snapshot (Coverage + Anzahl Matches).
  // Beide sind optional — bei frischer Anfrage liegen sie noch nicht vor.
  const match = useApi(
    () => api.getMatch(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  const preise = useApi(
    () => api.getPreise(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  // Phase 98 — der deterministische QS-Bericht (Ampel/Coverage/Hinweise).
  // Maßgeblich sobald die Pipeline durch ist; davor lokale Heuristik (s.u.).
  const qs = useApi(
    () => api.getQs(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );

  const s = summary.state.status === "ok" ? summary.state.data : null;
  const angebotData = angebot.state.status === "ok" ? angebot.state.data : null;
  const klaerData = klaer.state.status === "ok" ? klaer.state.data : null;
  const matchData = match.state.status === "ok" ? match.state.data : null;
  const preiseData = preise.state.status === "ok" ? preise.state.data : null;
  const qsData = qs.state.status === "ok" ? qs.state.data : null;
  const klaerOffen = klaerData ? klaerData.punkte.filter((p) => !p.resolved).length : 0;
  const angebotFertig = phasenVorhanden.includes(6);
  const positionenAnzahl = angebotData?.positionenAnzahl ?? "—";

  // QS-Snapshot: Phase 98 (98-qs.json) ist die maßgebliche Quelle, sobald die
  // Pipeline durch ist. Liegt sie noch nicht vor (frische/teilweise Anfrage),
  // fällt das Widget auf eine lokale Heuristik aus den vorhandenen Phasen
  // zurück — mit derselben Schwellen-Logik wie der qs-agent, damit die Anzeige
  // nicht springt. Die Klärpunkt-Aufschlüsselung (hoch/mittel/niedrig) zählt in
  // beiden Fällen live aus 97 (Phase 98 trägt nur die Summe).
  const klaerOffenHoch = klaerData
    ? klaerData.punkte.filter((p) => !p.resolved && p.prioritaet === "hoch")
        .length
    : 0;
  const klaerOffenMittel = klaerData
    ? klaerData.punkte.filter((p) => !p.resolved && p.prioritaet === "mittel")
        .length
    : 0;
  const klaerOffenNiedrig = klaerData
    ? klaerData.punkte.filter((p) => !p.resolved && p.prioritaet === "niedrig")
        .length
    : 0;
  const klaerGeloest = klaerData
    ? klaerData.punkte.filter((p) => p.resolved).length
    : 0;
  // Lokale Counters (Fallback vor dem Lauf).
  const matchGesamtLokal = matchData?.positionen.length ?? 0;
  const matchMitMatchLokal = matchData?.positionenMitMatch ?? 0;
  const preisbarLokal = preiseData
    ? preiseData.positionen.filter((p) => p.bepreisbar).length
    : 0;
  const coverageLokal =
    matchGesamtLokal > 0 ? preisbarLokal / matchGesamtLokal : 1;
  const ampelLokal: "gruen" | "gelb" | "rot" =
    klaerOffenHoch > 0 || (matchGesamtLokal > 0 && coverageLokal < 0.5)
      ? "rot"
      : klaerOffen > 0 || (matchGesamtLokal > 0 && coverageLokal < 0.9)
        ? "gelb"
        : "gruen";

  // Phase 98 ist maßgeblich, sobald vorhanden — sonst lokale Heuristik.
  const qsSource: "phase98" | "lokal" = qsData ? "phase98" : "lokal";
  const matchGesamt = qsData?.positionenGesamt ?? matchGesamtLokal;
  const matchMitMatch = qsData?.positionenMitMatch ?? matchMitMatchLokal;
  const preisbar = qsData?.positionenBepreisbar ?? preisbarLokal;
  const coverage = matchGesamt > 0 ? preisbar / matchGesamt : qsData ? 0 : 1;
  const ampel: "gruen" | "gelb" | "rot" = qsData?.ampel ?? ampelLokal;
  const hinweise = qsData?.hinweise ?? [];
  const showQs = qsData !== null || matchData !== null || klaerData !== null;
  const [previewFile, setPreviewFile] = useState<{ relPath: string; fileName: string } | null>(null);
  const [lvOpen, setLvOpen] = useState(false);
  const lvOpenable = phasenVorhanden.includes(3);

  return (
    <div className="ueber">
      <PdfKlassifikationBox rohdaten={rohdaten} tenantId={tenantId} onChanged={onRefetch} />

      <div className="metric-grid">
        <MetricCard
          label="KUNDENARTIKEL"
          value={s ? String(s.artikel.gesamt) : "—"}
          sub={s ? `${s.artikel.warengruppen.length} Warengruppen` : "Stammdaten laden …"}
        />
        <MetricCard
          label="KONDITIONEN"
          value={s ? String(s.konditionen.gesamt) : "—"}
          sub={
            s
              ? `${s.konditionen.kundenIds.length} Kunde${s.konditionen.kundenIds.length === 1 ? "" : "n"} hinterlegt`
              : "Stammdaten laden …"
          }
        />
        <MetricCard
          label="TENANT-MARGE"
          value={s ? `${s.tenant.margeProzent}%` : "—"}
          sub={s ? `Default · alle Positionen · ${s.tenant.waehrung}` : "—"}
          accent
        />
        <MetricCard
          label="POSITIONEN"
          value={String(positionenAnzahl)}
          sub={
            lvOpenable
              ? angebotFertig
                ? "LV konsolidiert · Klick für Details"
                : "Klick für Details"
              : "Pipeline noch nicht durch"
          }
          onClick={lvOpenable ? () => setLvOpen(true) : undefined}
        />
      </div>

      {showQs && (
        <QsBlock
          ampel={ampel}
          source={qsSource}
          hinweise={hinweise}
          klaerOffenHoch={klaerOffenHoch}
          klaerOffenMittel={klaerOffenMittel}
          klaerOffenNiedrig={klaerOffenNiedrig}
          klaerGeloest={klaerGeloest}
          matchGesamt={matchGesamt}
          matchMitMatch={matchMitMatch}
          preisbar={preisbar}
          coverage={coverage}
        />
      )}

      <SourceBlock
        rohdaten={rohdaten}
        onPdfOpen={(relPath, fileName) => setPreviewFile({ relPath, fileName })}
        tenantId={tenantId}
        anfrageId={anfrageId}
      />

      <PipelineBlock phasenVorhanden={phasenVorhanden} />

      <div className="two-col">
        <NoticeBlock
          tenantId={tenantId}
          kundeName={rohdaten.kunde.name}
          klaerOffen={klaerOffen}
        />
        {angebotFertig && angebotData ? (
          <ReadyBlock
            tenantId={tenantId}
            anfrageId={anfrageId}
            summeNetto={angebotData.summeNetto}
            waehrung={s?.tenant.waehrung ?? "EUR"}
            artefakte={angebotData.artefakte}
          />
        ) : (
          <NotReadyBlock onRunRequest={onRunRequest} />
        )}
      </div>

      {previewFile && (
        <PdfPreviewModal
          url={api.eingangFileUrl(tenantId, anfrageId, previewFile.relPath)}
          fileName={previewFile.fileName}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {lvOpen && (
        <LvTreeModal
          tenantId={tenantId}
          anfrageId={anfrageId}
          onClose={() => setLvOpen(false)}
        />
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  accent,
  onClick,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="metric-label uppercase-mini">{label}</div>
      <div className="metric-value mono">{value}</div>
      <div className="metric-sub">{sub}</div>
      <div className="metric-deco" aria-hidden>
        <svg viewBox="0 0 80 80" width="80" height="80">
          <circle cx="40" cy="40" r="30" stroke="currentColor" strokeOpacity="0.06" fill="none" />
          <circle cx="40" cy="40" r="22" stroke="currentColor" strokeOpacity="0.1" fill="none" />
          <circle cx="40" cy="40" r="14" stroke="currentColor" strokeOpacity="0.16" fill="none" />
        </svg>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={`metric ${accent ? "acc" : ""}`}
        onClick={onClick}
        style={{
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
        }}
      >
        {content}
      </button>
    );
  }
  return <div className={`metric ${accent ? "acc" : ""}`}>{content}</div>;
}

function QsBlock({
  ampel,
  source,
  hinweise,
  klaerOffenHoch,
  klaerOffenMittel,
  klaerOffenNiedrig,
  klaerGeloest,
  matchGesamt,
  matchMitMatch,
  preisbar,
  coverage,
}: {
  ampel: "gruen" | "gelb" | "rot";
  source: "phase98" | "lokal";
  hinweise: string[];
  klaerOffenHoch: number;
  klaerOffenMittel: number;
  klaerOffenNiedrig: number;
  klaerGeloest: number;
  matchGesamt: number;
  matchMitMatch: number;
  preisbar: number;
  coverage: number;
}) {
  const ampelColor =
    ampel === "rot"
      ? "var(--bad)"
      : ampel === "gelb"
        ? "var(--warn)"
        : "var(--ok)";
  const ampelLabel =
    ampel === "rot"
      ? "BLOCKER"
      : ampel === "gelb"
        ? "ZU PRUEFEN"
        : "BEREIT";
  const klaerOffen = klaerOffenHoch + klaerOffenMittel + klaerOffenNiedrig;
  return (
    <section className="block">
      <div className="block-head">
        <span className="uppercase-mini">// QS · QUALITAETS-SNAPSHOT</span>
        <span
          className="uppercase-mini"
          style={{ color: "var(--fg-faint)", marginLeft: 8 }}
          title={
            source === "phase98"
              ? "Aus 98-qs.json (Phase 98, deterministisch)"
              : "Lokale Schätzung aus den vorhandenen Phasen — Phase 98 lief noch nicht"
          }
        >
          {source === "phase98" ? "· PHASE 98" : "· VORLÄUFIG"}
        </span>
        <span className="block-sep" />
        <span
          className="uppercase-mini"
          style={{
            color: ampelColor,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: ampelColor,
              boxShadow: `0 0 8px ${ampelColor}`,
            }}
          />
          {ampelLabel}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          padding: "14px 18px",
        }}
      >
        <div>
          <div
            className="uppercase-mini"
            style={{ color: "var(--fg-dim)", marginBottom: 8 }}
          >
            Klaerpunkte
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            <QsRow
              label="HOCH"
              value={klaerOffenHoch}
              tone={klaerOffenHoch > 0 ? "bad" : "fade"}
            />
            <QsRow
              label="MITTEL"
              value={klaerOffenMittel}
              tone={klaerOffenMittel > 0 ? "warn" : "fade"}
            />
            <QsRow
              label="NIEDRIG"
              value={klaerOffenNiedrig}
              tone={klaerOffenNiedrig > 0 ? "" : "fade"}
            />
            <QsRow
              label={`GELOEST (${klaerGeloest})`}
              value={null}
              tone="fade"
            />
          </div>
        </div>
        <div>
          <div
            className="uppercase-mini"
            style={{ color: "var(--fg-dim)", marginBottom: 8 }}
          >
            Match + Bepreisung
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            <QsRow
              label="POSITIONEN GESAMT"
              value={matchGesamt}
              tone="fade"
            />
            <QsRow
              label="MIT MATCH"
              value={matchMitMatch}
              tone={matchMitMatch === matchGesamt && matchGesamt > 0 ? "ok" : ""}
            />
            <QsRow
              label="BEPREISBAR"
              value={preisbar}
              tone={preisbar === matchGesamt && matchGesamt > 0 ? "ok" : ""}
            />
            <QsRow
              label="COVERAGE"
              value={`${Math.round(coverage * 100)}%`}
              tone={coverage >= 0.9 ? "ok" : coverage >= 0.5 ? "warn" : "bad"}
            />
          </div>
        </div>
      </div>
      {hinweise.length > 0 && (
        <div
          style={{
            padding: "12px 18px 4px",
            display: "grid",
            gap: 6,
          }}
        >
          <div className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
            Auffaelligkeiten
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 12,
              color: "var(--fg-mute)",
              display: "grid",
              gap: 4,
            }}
          >
            {hinweise.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </div>
      )}
      {klaerOffen > 0 && (
        <div
          style={{
            padding: "10px 18px 14px",
            fontSize: 12,
            color: "var(--fg-mute)",
          }}
        >
          {klaerOffen} offene{" "}
          {klaerOffen === 1 ? "Klaerpunkt" : "Klaerpunkte"} — abarbeiten im{" "}
          <em>Klaerpunkte</em>-Tab.
        </div>
      )}
    </section>
  );
}

function QsRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string | null;
  tone: "ok" | "warn" | "bad" | "fade" | "";
}) {
  const color =
    tone === "ok"
      ? "var(--ok)"
      : tone === "warn"
        ? "var(--warn)"
        : tone === "bad"
          ? "var(--bad)"
          : tone === "fade"
            ? "var(--fg-faint)"
            : "var(--fg)";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
        {label}
      </span>
      {value !== null && (
        <span className="mono" style={{ color, fontVariantNumeric: "tabular-nums" }}>
          {value}
        </span>
      )}
    </div>
  );
}

function SourceBlock({
  rohdaten,
  onPdfOpen,
  tenantId,
  anfrageId,
}: {
  rohdaten: AnfrageRohdaten;
  onPdfOpen: (relPath: string, fileName: string) => void;
  tenantId: string;
  anfrageId: string;
}) {
  void tenantId;
  void anfrageId;
  if (rohdaten.dateien.length === 0) {
    return (
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// QUELLE · UPLOAD</span>
          <span className="block-sep" />
          <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
            keine Dateien
          </span>
        </div>
      </section>
    );
  }

  return (
    <section className="block">
      <div className="block-head">
        <span className="uppercase-mini">// QUELLE · UPLOAD</span>
        <span className="block-sep" />
        <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
          {rohdaten.dateien.length} Datei{rohdaten.dateien.length === 1 ? "" : "en"}
        </span>
      </div>
      <div className="source-grid">
        {rohdaten.dateien.map((d) => {
          const ext = (d.relPath.split(".").pop() ?? "").toLowerCase();
          const isLv = /^(x8[1-6]|gaeb|xml)$/.test(ext);
          const klass = d.pdfKlassifikation;
          const isPdf = ext === "pdf";
          const label = isLv
            ? "LV ERKANNT"
            : klass?.erkanntAls === "lv"
              ? "LV (PDF)"
              : klass?.erkanntAls === "bauplan"
                ? "BAUPLAN"
                : klass?.erkanntAls === "unklar"
                  ? "UNKLAR"
                  : isPdf
                    ? "PDF"
                    : ext.toUpperCase();
          const pillTone = isLv || klass?.erkanntAls === "lv" ? "ok" : klass?.erkanntAls === "bauplan" ? "acc" : klass?.erkanntAls === "unklar" ? "warn" : "";
          const icoColor = isLv
            ? "var(--ok)"
            : klass?.erkanntAls === "bauplan"
              ? "var(--acc)"
              : "var(--fg-dim)";
          const fileName = d.relPath.split("/").pop() ?? d.relPath;
          const canPreview = ext === "pdf";

          const inner = (
            <>
              <div className="source-file-l">
                <div
                  className="file-ico-box mono"
                  style={{
                    color: icoColor,
                    borderColor:
                      icoColor === "var(--acc)"
                        ? "oklch(0.78 0.16 215 / 0.4)"
                        : icoColor === "var(--ok)"
                          ? "oklch(0.78 0.16 152 / 0.4)"
                          : "var(--line)",
                  }}
                >
                  .{ext}
                </div>
                <div>
                  <div className="source-name mono">{fileName}</div>
                  <div className="source-meta uppercase-mini">{ext.toUpperCase()}</div>
                </div>
              </div>
              <span className={`pill ${pillTone}`}>
                <span className="dot" />
                {label}
              </span>
            </>
          );
          return canPreview ? (
            <button
              key={d.relPath}
              type="button"
              className="source-file"
              onClick={() => onPdfOpen(d.relPath, fileName)}
              style={{
                cursor: "pointer",
                width: "100%",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
              }}
              title="Klick: PDF in Vorschau öffnen"
            >
              {inner}
            </button>
          ) : (
            <div key={d.relPath} className="source-file">
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PipelineBlock({ phasenVorhanden }: { phasenVorhanden: number[] }) {
  const set = new Set(phasenVorhanden);
  const totalDone = PHASES.filter((p) => set.has(p.id)).length;

  return (
    <section className="block">
      <div className="block-head">
        <span className="uppercase-mini">// PIPELINE-PHASEN</span>
        <span className="block-sep" />
        <span
          className="mono"
          style={{
            color: totalDone === PHASES.length ? "var(--ok)" : "var(--fg-dim)",
            fontSize: 11,
          }}
        >
          ● {totalDone}/{PHASES.length} vorhanden
        </span>
      </div>
      <div className="pipeline">
        {PHASES.map((p, i) => {
          const done = set.has(p.id);
          return (
            <div key={p.id} className={`phase ${done ? "phase-done" : "phase-pending"}`}>
              <div className="phase-rail">
                <div className="phase-bullet">
                  {done ? <Icon name="check" size={12} /> : <Icon name="dot" size={6} />}
                </div>
                {i < PHASES.length - 1 && <div className="phase-connector" />}
              </div>
              <div className="phase-body">
                <div className="phase-head">
                  <span className="phase-code mono">{p.code}</span>
                  <span className="phase-label">{p.label}</span>
                  <span className="phase-model uppercase-mini">{p.model}</span>
                </div>
                <div className="phase-status uppercase-mini">
                  {done ? "vorhanden · gültig" : "fehlt"}
                </div>
              </div>
              <div className="phase-action" />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function NoticeBlock({
  tenantId,
  kundeName,
  klaerOffen,
}: {
  tenantId: string;
  kundeName: string;
  klaerOffen: number;
}) {
  const kundeId = slugifyKundeId(kundeName);
  const summary = useApi(
    () => api.getStammdatenSummary(tenantId, kundeId),
    [tenantId, kundeId]
  );
  if (summary.state.status !== "ok") {
    return (
      <section className="block notice">
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
            // KONDITIONEN-CHECK
          </span>
        </div>
        <div className="notice-body">
          <div className="dim">Stammdaten werden geladen …</div>
        </div>
      </section>
    );
  }
  const s = summary.state.data;
  const g = s.konditionen.gefiltert;
  const greift = !!g && (g.rabatte > 0 || g.sonderpreise > 0);
  const bekannteIds = s.konditionen.kundenIds;
  const kein_Match = bekannteIds.length > 0 && !bekannteIds.includes(kundeId);

  return (
    <section className="block notice">
      <div className="block-head">
        <span className="uppercase-mini" style={{ color: greift ? "var(--ok)" : "var(--warn)" }}>
          // KONDITIONEN-CHECK
        </span>
      </div>
      <div className="notice-body">
        <div>
          Konditionen für Kunde <span className="quote">„{kundeName}"</span>{" "}
          <span className="mono dim">kundeId: {kundeId}</span>
        </div>
        <div className="kpi-row">
          <span>
            <span className="mono">{g?.rabatte ?? 0}</span> Rabatt
            {(g?.rabatte ?? 0) === 1 ? "" : "e"}
          </span>
          <span>
            <span className="mono">{g?.sonderpreise ?? 0}</span> Sonderpreis
            {(g?.sonderpreise ?? 0) === 1 ? "" : "e"}
          </span>
          {klaerOffen > 0 && (
            <span>
              <span className="mono" style={{ color: "var(--warn)" }}>
                {klaerOffen}
              </span>{" "}
              Klärpunkt{klaerOffen === 1 ? "" : "e"} offen
            </span>
          )}
        </div>
        {kein_Match && (
          <div className="warn-line">
            Bekannte kundeIds im Stamm:{" "}
            <span className="mono">{bekannteIds.join(", ")}</span>. Match auf{" "}
            <span className="mono">{kundeId}</span> greift nicht — eventuell
            Tippfehler oder neuer Kunde?
          </div>
        )}
        <div className="stand-line mono">
          Stand · kundenartikel {fmtDate(s.lastModified.kundenartikel)} · konditionen{" "}
          {fmtDate(s.lastModified.konditionsmatrix)} · config{" "}
          {fmtDate(s.lastModified.config)}
        </div>
      </div>
    </section>
  );
}

function ReadyBlock({
  tenantId,
  anfrageId,
  summeNetto,
  waehrung,
  artefakte,
}: {
  tenantId: string;
  anfrageId: string;
  summeNetto: number;
  waehrung: string;
  artefakte: Array<{ format: string; groesseBytes: number; relPath: string }>;
}) {
  return (
    <section className="block ready">
      <div className="block-head">
        <span className="uppercase-mini" style={{ color: "var(--ok)" }}>
          // ANGEBOT BEREIT
        </span>
      </div>
      <div className="ready-body">
        <div className="ready-line">
          Excel- und PDF-Export vorhanden. Summe netto{" "}
          <span className="mono" style={{ color: "var(--fg)" }}>
            {fmtEur(summeNetto, waehrung)}
          </span>
          .
        </div>
        <div className="dl-row">
          {artefakte.map((a) => (
            <a
              key={a.format}
              href={api.angebotDownloadUrl(tenantId, anfrageId, a.format as "xlsx" | "pdf")}
              className={`btn ${a.format === "xlsx" ? "primary" : ""}`}
            >
              <Icon name="download" size={14} />{" "}
              {a.format.toUpperCase()} herunterladen
            </a>
          ))}
        </div>
        <div className="file-meta mono">
          {artefakte.map((a) => (
            <span key={a.relPath}>
              {a.relPath.split("/").pop()} · {fmtBytes(a.groesseBytes)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function NotReadyBlock({ onRunRequest }: { onRunRequest: () => void }) {
  return (
    <section className="block ready">
      <div className="block-head">
        <span className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
          // ANGEBOT NOCH NICHT FERTIG
        </span>
      </div>
      <div className="ready-body">
        <div className="ready-line">
          Pipeline noch nicht durch Phase 6. Sobald{" "}
          <span className="mono dim">06-angebot.{`{json, xlsx, pdf}`}</span> erzeugt
          ist, erscheinen hier die Download-Links.
        </div>
        <div>
          <button type="button" className="btn primary" onClick={onRunRequest}>
            <Icon name="play" size={14} /> Pipeline starten
          </button>
        </div>
      </div>
    </section>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtEur(n: number, waehrung: string): string {
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${waehrung}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Spiegel von `slugifyKundeId` aus core/agents/bepreisungs-agent.
 * (Sync halten bei Schema-Aenderung.)
 */
function slugifyKundeId(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
